import { PlexUserRelationState, SlateNode, TaskUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { createStyles, makeStyles } from '@material-ui/core';
import MuiCheckIcon from '@material-ui/icons/Check';
import MuiCloseIcon from '@material-ui/icons/Close';
import MuiCreateIcon from '@material-ui/icons/Create';
import React, { useState } from 'react';

import { useUpdateTaskMutation } from '../../../../graphql/gen/types';
import { colorPalette, spacings } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { Entities } from '../../../shared/slate/entities';
import { ReadOnlyEditor } from '../../../shared/slate/ReadOnlyEditor';
import { SlateEditor, SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { canEditTask } from '../../permissions';
import { InlineModal } from './common/InlineModal';

type TaskBodyViewProps =
  | {
      mode: 'edit_inline';
      value: SlateEditorValue;
      initialEntities: Entities;
      saving: boolean;
      onChange: (value: SlateEditorValue) => void;
      onSave: () => void;
      onCancel: () => void;
    }
  | {
      mode: 'edit';
      value: SlateEditorValue;
      initialEntities: Entities;
      saving: boolean;
      onChange: (value: SlateEditorValue) => void;
    }
  | {
      mode: 'view';
      value: SlateNode[];
      initialEntities: Entities;
      onEdit: (() => void) | null;
    };

export const TaskBodyView = React.forwardRef(function TaskBodyView(
  props: TaskBodyViewProps,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement {
  const classes = useStyles();

  return (
    <div className={classes.root} ref={ref}>
      <div className={classes.descriptionHeader}>
        <span className={classes.label}>Description</span>
        {props.mode === 'view' && props.onEdit != null && (
          <PlexIconButton
            className={classes.editButton}
            onClick={props.onEdit}
            MuiIcon={MuiCreateIcon}
            color={ButtonColor.Purple}
            tooltip={'Edit Description'}
          />
        )}
        {props.mode === 'edit_inline' && (
          <>
            <PlexIconButton
              disabled={props.saving}
              className={classes.editButton}
              onClick={props.onSave}
              MuiIcon={MuiCheckIcon}
              color={ButtonColor.Purple}
              tooltip={'Save'}
            />
            <PlexIconButton
              disabled={props.saving}
              className={classes.editButton}
              onClick={props.onCancel}
              MuiIcon={MuiCloseIcon}
              color={ButtonColor.Purple}
              tooltip={'Cancel'}
            />
          </>
        )}
      </div>
      {props.mode === 'view' && (
        <ReadOnlyEditor className={classes.slateView} value={props.value} entities={props.initialEntities} />
      )}
      {(props.mode === 'edit' || props.mode === 'edit_inline') && (
        <SlateEditor
          className={classes.slateEditor}
          value={props.value}
          initialEntities={props.initialEntities}
          title=""
          onChange={props.onChange}
          primaryLink={null}
          onChangePrimaryLink={() => {
            /* unused */
          }}
        />
      )}
    </div>
  );
});

type Props = {
  id: string;
  value: SlateNode[];
  initialEntities: Entities;
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
};

type State = { type: 'initial' } | { type: 'editing'; value: SlateEditorValue };

export const TaskBody = ({ id, plexRelation, taskRelation, value, initialEntities }: Props): React.ReactElement => {
  const classes = useStyles();
  const [state, setState] = useState<State>({ type: 'initial' });
  const [updateTask, { loading }] = useUpdateTaskMutation();

  return (
    <>
      {state.type === 'initial' ? (
        <TaskBodyView
          mode="view"
          value={value}
          initialEntities={initialEntities}
          onEdit={
            canEditTask({ plexRelation, taskRelation })
              ? () => {
                  setState({ type: 'editing', value: SlateEditorValue.from(value) });
                }
              : null
          }
        />
      ) : (
        <InlineModal
          onClose={() => {
            setState({ type: 'initial' });
          }}
          style={{
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className={classes.editableRoot}>
            <TaskBodyView
              mode="edit_inline"
              value={state.value}
              initialEntities={initialEntities}
              saving={loading}
              onChange={(value) => setState({ ...state, value })}
              onSave={() => {
                updateTask({ variables: { task: { id, body: state.value.value } } }).then(() => {
                  setState({ type: 'initial' });
                });
              }}
              onCancel={() => {
                setState({ type: 'initial' });
              }}
            />
          </div>
        </InlineModal>
      )}
    </>
  );
};

const DESCRIPTION_HEADER_MARGIN_BOTTOM = spacings.small;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      marginBottom: spacings.xlarge,
    },
    editableRoot: {
      flex: 1,
      display: 'flex',

      '&:focus': {
        // Hide default focus outline across the modal boundries
        outline: '0px',
      },
    },
    descriptionHeader: {
      display: 'inline-flex',
      alignItems: 'center',
      marginBottom: DESCRIPTION_HEADER_MARGIN_BOTTOM,
    },
    label: {
      fontSize: 15,
      fontFamily: 'Inter',
      letterSpacing: 'normal',

      marginRight: spacings.small,
    },
    title: {
      display: 'inline',
      color: colorPalette.dark._900,
      fontWeight: 'bold',
    },
    editButton: {
      display: 'inline',
      marginLeft: spacings.small,
    },
    slateEditor: {
      border: '2px solid #EAEAEA',
      // Align start of text to be border (2px) + padding
      marginLeft: -2 - spacings.xlarge,
    },
    slateView: {
      // Align top of text to be border (2px) + margin
      marginTop: 2 + DESCRIPTION_HEADER_MARGIN_BOTTOM,
    },
  })
);
